import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Bell,
  BookOpen,
  ChevronRight,
  Globe2,
  Lightbulb,
  LockKeyhole,
  Mail,
  MessageCircle,
  Paperclip,
  Phone,
  Search,
} from 'lucide-react';

import { AuthGuard } from '../../app/AuthGuard';
import { RoleSettingsCard } from '../../ui/RoleSettingsCard/RoleSettingsCard';
import { useFigmaBreakpoint, type FigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import {
  customerCabinetApi,
  type CustomerProfileDto,
  type DisputeDto,
  type FileAssetDto,
  type NotificationDto,
  type OfferDto,
  type ReviewDto,
  type TransportDto,
} from '../../features/customerCabinet/api';
import { formatNotification } from '../../features/chat/messageUtils';
import {
  CustomerCabinetLayout,
  navigateCustomer,
  type CustomerCabinetScreenId,
  type CustomerFigmaNodes,
} from '../../layouts/CustomerCabinet/CustomerCabinetLayout';
import {
  Button,
  Checkbox,
  LocaleSettingsCard,
  NotificationMatrixCard,
  ProfileLink,
  SecurityCard,
  Stars,
  SupportHelpView,
  Tag,
  Tile,
  UnifiedNotificationList,
  type StarsRating,
} from '../../ui';
import { useChatStatus } from '../../state/chatStatus';
import { useLanguage } from '../../i18n/LanguageProvider';
import type { AppLanguage } from '../../i18n/locale';
import {
  CustomerPageHeading,
  CustomerStatus,
  dateTime,
  formatRatingScore,
  hashNumber,
  initials,
  relativeTime,
  transportsCountLabel,
} from './customerCabinetShared';
import './customer-cabinet.css';

export type CustomerAccountScreenId = Extract<
  CustomerCabinetScreenId,
  'review' | 'dispute' | 'notifications' | 'settings' | 'customer-help'
>;

const FIGMA_NODES: Record<CustomerAccountScreenId, CustomerFigmaNodes> = {
  review: { '1920': '2680:39561', '1440': '2680:40300', '768': '2593:17345', '375': '2594:17345' },
  dispute: { '1920': '2680:39852', '1440': '2680:40590', '768': '2615:17345', '375': '2615:17503' },
  notifications: { '1920': '2680:39767', '1440': '2680:40506', '768': '2597:17345', '375': '2597:17483' },
  settings: { '1920': '2680:39667', '1440': '2680:40406', '768': '2595:17345', '375': '2596:17345' },
  'customer-help': { '1920': '2719:36145', '1440': '2719:36809', '768': '2593:17345', '375': '2594:17345' },
};

const GEOMETRY: Record<CustomerAccountScreenId, {
  height: Record<'desktop' | 'tablet' | 'mobile', number>;
  offset: Record<'desktop' | 'tablet' | 'mobile', number>;
}> = {
  review: { height: { desktop: 1176, tablet: 1146, mobile: 927 }, offset: { desktop: 0, tablet: 43.5, mobile: 52.5 } },
  dispute: { height: { desktop: 1239, tablet: 927, mobile: 1229 }, offset: { desktop: 0, tablet: 20, mobile: 13.5 } },
  notifications: { height: { desktop: 977, tablet: 464, mobile: 453 }, offset: { desktop: 0, tablet: 0, mobile: 0 } },
  settings: { height: { desktop: 1540, tablet: 1482, mobile: 1072 }, offset: { desktop: 0, tablet: 29.5, mobile: 32.5 } },
  'customer-help': { height: { desktop: 800, tablet: 600, mobile: 600 }, offset: { desktop: 0, tablet: 0, mobile: 0 } },
};

function AccountLayout({ children, screen }: { children: ReactNode; screen: CustomerAccountScreenId }) {
  const geometry = GEOMETRY[screen];
  return (
    <CustomerCabinetLayout
      contentHeight={geometry.height}
      figmaNode={FIGMA_NODES[screen]}
      screen={screen}
      topOffset={geometry.offset}
    >
      {children}
    </CustomerCabinetLayout>
  );
}

function RatingPicker({ label, onChange, value }: {
  label: string;
  onChange: (value: number) => void;
  value: number;
}) {
  return (
    <div className="customer-rating-picker">
      <span>{label}</span>
      <div>
        <Stars mode="Light" rating={value as StarsRating} />
        <div className="customer-rating-picker__hits">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              aria-label={`${label}: ${rating} из 5`}
              aria-pressed={rating === value}
              key={rating}
              type="button"
              onClick={() => onChange(rating)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewView() {
  const { language } = useLanguage();
  const requestedTransportId = hashNumber('id') || hashNumber('transport');
  const requestedRequestId = hashNumber('request');
  const [transport, setTransport] = useState<TransportDto | null>(null);
  const [offer, setOffer] = useState<OfferDto | null>(null);
  const [receivedReview, setReceivedReview] = useState<ReviewDto | null>(null);
  const [paymentConfirmedAt, setPaymentConfirmedAt] = useState<string | null>(null);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [ratings, setRatings] = useState({ overall: 5, care: 5, timing: 5, photos: 5, communication: 5 });
  const [comment, setComment] = useState('');
  const reviewMediaInput = useRef<HTMLInputElement>(null);
  const [reviewMedia, setReviewMedia] = useState<FileAssetDto[]>([]);
  const [submitError, setSubmitError] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'uploading' | 'submitting' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [transports, offers, reviews, notifications, chats] = await Promise.all([
        customerCabinetApi.transports(),
        customerCabinetApi.offers(),
        customerCabinetApi.reviews(),
        customerCabinetApi.notifications().catch(() => []),
        customerCabinetApi.chats().catch(() => []),
      ]);
      const nextTransport = (requestedTransportId ? transports.find((item) => item.id === requestedTransportId) : null)
        ?? (requestedRequestId ? transports.find((item) => item.request_id === requestedRequestId) : null)
        ?? transports.find((item) => item.status === 'closed' || item.customer_received_at)
        ?? null;
      if (!active) return;
      setTransport(nextTransport);
      setOffer(offers.find((item) => item.request === nextTransport?.request_id && item.carrier === nextTransport?.carrier_id) ?? null);
      setReceivedReview(reviews.find((item) => item.transport === nextTransport?.id && item.author_role === 'carrier') ?? null);
      const paymentChat = chats.find((item) => item.deal === nextTransport?.deal);
      const paymentNotification = notifications.find((item) => item.notification_type === 'carrier_confirmed_payment'
        && item.target_screen === 'chat' && item.target_id === String(paymentChat?.id ?? nextTransport?.deal));
      setPaymentConfirmedAt(paymentNotification?.created_at ?? null);
      setAlreadyReviewed(reviews.some((item) => item.transport === nextTransport?.id && item.author_role === 'customer'));
      setState('ready');
    };
    load().catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [requestedTransportId, requestedRequestId]);

  const media = useMemo(
    () => transport?.checkpoints.flatMap((checkpoint) => checkpoint.media_files.map((file) => ({ file, title: checkpoint.title }))) ?? [],
    [transport],
  );
  const mediaTags = useMemo(
    () => Array.from(new Map(media.map((item) => [item.title, item])).values()),
    [media],
  );

  async function uploadReviewMedia(files: FileList | null) {
    if (!files?.length) return;
    setState('uploading');
    try {
      const uploaded = await Promise.all(
        Array.from(files).map((file) => customerCabinetApi.uploadReviewMedia(file)),
      );
      setReviewMedia((current) => [...current, ...uploaded]);
      setState('ready');
    } catch {
      setState('error');
    } finally {
      if (reviewMediaInput.current) reviewMediaInput.current.value = '';
    }
  }

  async function submit() {
    if (!transport || alreadyReviewed || (!transport.customer_received_at && transport.status !== 'closed')) return;
    setSubmitError('');
    setState('submitting');
    try {
      await customerCabinetApi.createReview({
        transport: transport.id,
        author_role: 'customer',
        rating_overall: ratings.overall,
        criteria_ratings: {
          care: ratings.care,
          timing: ratings.timing,
          photos: ratings.photos,
          communication: ratings.communication,
        },
        comment,
        attached_photos: Array.from(new Set([
          ...media.map(({ file }) => file.id),
          ...reviewMedia.map((file) => file.id),
        ])),
      });
      setAlreadyReviewed(true);
      window.location.hash = 'platform-feedback';
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Не удалось опубликовать отзыв. Попробуйте ещё раз.');
      setState('error');
    }
  }

  return (
    <AccountLayout screen="review">
      <div className="customer-review" data-state={state}>
        {transport ? (
          <>
            <CustomerPageHeading
              figmaNode="2680:40304"
              title="Оставьте отзыв"
              subtitle={(
                <>
                  <span>Перевозка завершена</span>
                  <i className="customer-review__code-bullet" />
                  <span className="customer-review__request-code">#{transport.request_code}</span>
                  <i />
                  <span>{transport.from_city} → {transport.to_city}</span>
                  {receivedReview ? (
                    <>
                      <i className="customer-review__received-bullet" />
                      <span className="customer-review__received-note">Перевозчик уже оставил отзыв о вас.</span>
                    </>
                  ) : null}
                </>
              )}
            />
            {transport.payment_status === 'carrier_confirmed' ? (
              <div className="customer-review__banner" data-figma-node="2680:40322">
                <span>✓ {transport.carrier_name} подтвердил оплату{paymentConfirmedAt ? ` ${dateTime(paymentConfirmedAt)}` : ''}</span>
                <span>✓ Сделка закрыта: {transport.carrier_name} подтвердил получение оплаты{paymentConfirmedAt ? ` ${dateTime(paymentConfirmedAt)}` : ''}</span>
                <span>✓ {transport.carrier_name} подтвердил оплату{paymentConfirmedAt ? ` ${dateTime(paymentConfirmedAt)}` : ''}</span>
              </div>
            ) : null}
            <section className="customer-review__form" data-figma-node="2680:40323">
              <strong className="customer-review__eyebrow">Ваша оценка перевозчика</strong>
              <div className="customer-review__carrier">
                <span><ProfileLink profileId={transport.carrier_id} role="carrier">{initials(transport.carrier_name)}</ProfileLink></span>
                <div>
                  <strong><ProfileLink profileId={transport.carrier_id} role="carrier">{transport.carrier_name}</ProfileLink></strong>
                  <small>★ {formatRatingScore(offer?.carrier_rating)} · {transportsCountLabel(offer?.carrier_completed_transports_count ?? 0, language)}</small>
                </div>
              </div>
              <div className="customer-review__ratings">
                <RatingPicker label="Общая оценка" value={ratings.overall} onChange={(overall) => setRatings((current) => ({ ...current, overall }))} />
                <RatingPicker label="Бережность к питомцу" value={ratings.care} onChange={(care) => setRatings((current) => ({ ...current, care }))} />
                <RatingPicker label="Соблюдение сроков" value={ratings.timing} onChange={(timing) => setRatings((current) => ({ ...current, timing }))} />
                <RatingPicker label="Качество отчётов с фото" value={ratings.photos} onChange={(photos) => setRatings((current) => ({ ...current, photos }))} />
                <RatingPicker label="Связь и общение" value={ratings.communication} onChange={(communication) => setRatings((current) => ({ ...current, communication }))} />
              </div>
              <label className="customer-review__comment"><span>Комментарий</span><input placeholder="Что понравилось..." value={comment} onChange={(event) => setComment(event.target.value)} /></label>
              <div className="customer-review__photos">
                <div className="customer-review__photos-head">
                  <span className="customer-review__photos-title">
                    <span>Фото с этой перевозки</span>
                    <span>Фото с этой перевозки (автоматически)</span>
                  </span>
                  <Tag className="customer-review__automatic" icon="no" label="автоматически" type="Awards" />
                </div>
                <p>
                  <span>Все фото, сделанные перевозчиком на этом маршруте, прикрепляются к отзыву и попадают в его публичное портфолио.</span>
                  <span>Все фото, сделанные перевозчиком на маршруте, прикрепляются к отзыву.</span>
                </p>
                <div className="customer-review__photo-tags">
                  {mediaTags.map(({ file, title }) => (
                    <a href={file.url} key={title} target="_blank" rel="noreferrer">
                      <Tag icon="no" label={title} type="Awards" />
                    </a>
                  ))}
                </div>
                <input
                  ref={reviewMediaInput}
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                  hidden
                  multiple
                  type="file"
                  onChange={(event) => void uploadReviewMedia(event.target.files)}
                />
                <button
                  className="customer-review__media-upload"
                  disabled={state === 'uploading' || alreadyReviewed}
                  type="button"
                  onClick={() => reviewMediaInput.current?.click()}
                >
                  <Paperclip aria-hidden="true" />
                  {state === 'uploading'
                    ? 'Загрузка...'
                    : reviewMedia.length
                      ? `Добавлено файлов: ${reviewMedia.length}`
                      : 'Добавить фото или видео к отзыву'}
                </button>
              </div>
            </section>
            {receivedReview ? (
              <section className="customer-review__received" data-figma-node="2680:40391">
                <strong>Отзыв перевозчика о вас</strong>
                <div><span>{initials(transport.carrier_name)}</span><p><b><ProfileLink profileId={transport.carrier_id} role="carrier">{transport.carrier_name}</ProfileLink></b><Stars mode="Light" rating={Math.max(1, Math.min(5, Math.round(receivedReview.rating_overall))) as StarsRating} /></p></div>
                <blockquote>«{receivedReview.comment}»</blockquote>
              </section>
            ) : null}
            <form
              className="customer-review__actions"
              data-figma-node="2680:40319"
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              {submitError ? <p className="customer-review__submit-error" role="alert">{submitError}</p> : null}
              <button className="customer-primary" disabled={alreadyReviewed || state === 'submitting' || state === 'uploading'} type="submit">{alreadyReviewed ? 'Отзыв отправлен на модерацию' : state === 'submitting' ? 'Публикуем...' : 'Опубликовать отзыв'}</button>
              <button className="customer-outline" type="button" onClick={() => navigateCustomer('my-requests')}>Пропустить</button>
            </form>
          </>
        ) : null}
      </div>
    </AccountLayout>
  );
}

const DISPUTE_REASONS = [
  ['pet_condition', 'Состояние питомца', 'Питомец получен с признаками стресса, травмы или плохого ухода'],
  ['not_delivered', 'Не доставлен', 'Перевозчик пропал, перевозка не выполнена'],
  ['delivery_delay', 'Серьёзная задержка', 'Срыв сроков более чем на сутки без согласования'],
  ['terms', 'Нарушены условия', 'Не соблюдались договорённости (корм, переноска, отчёты)'],
  ['no_reports', 'Отсутствие отчётов', 'Не загружались фото с этапов перевозки'],
  ['other', 'Другое', 'Опишите свободно'],
] as const;

const DISPUTE_RESOLUTIONS = ['Полный возврат', 'Частичный возврат', 'Компенсация ущерба', 'Только разобраться'] as const;

const DISPUTE_BLOCK_NODES: Record<FigmaBreakpoint, {
  alert: string;
  back: string;
  card: string;
  content?: string;
  heading: string;
  actions: string;
}> = {
  '1920': { alert: '2680:39871', back: '2680:39854', card: '2680:39872', content: '2680:39870', heading: '2680:39857', actions: '2680:39899' },
  '1440': { alert: '2680:40608', back: '2680:40606', card: '2680:40609', content: '2680:40607', heading: '2680:40594', actions: '2680:40636' },
  '768': { alert: '2615:17450', back: '2615:17449', card: '2615:17452', heading: '2615:17446', actions: '2615:17498' },
  '375': { alert: '2615:17609', back: '2615:17608', card: '2615:17611', heading: '2615:17605', actions: '2615:17660' },
};

function DisputeView() {
  const breakpoint = useFigmaBreakpoint();
  const nodes = DISPUTE_BLOCK_NODES[breakpoint];
  const requestedDealId = hashNumber('deal');
  const fileInput = useRef<HTMLInputElement>(null);
  const [transport, setTransport] = useState<TransportDto | null>(null);
  const [existing, setExisting] = useState<DisputeDto | null>(null);
  const [reason, setReason] = useState<(typeof DISPUTE_REASONS)[number][0]>('pet_condition');
  const [resolution, setResolution] = useState<(typeof DISPUTE_RESOLUTIONS)[number]>('Только разобраться');
  const [description, setDescription] = useState('');
  const [evidence, setEvidence] = useState<number[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'uploading' | 'submitting' | 'success' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    Promise.all([customerCabinetApi.transports(), customerCabinetApi.disputes()]).then(([transports, disputes]) => {
      if (!active) return;
      const next = transports.find((item) => item.deal === requestedDealId) ?? transports[0] ?? null;
      setTransport(next);
      setExisting(disputes.find((item) => item.deal === next?.deal) ?? null);
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [requestedDealId]);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setState('uploading');
    try {
      const assets = await Promise.all(Array.from(files).map((file) => customerCabinetApi.uploadDisputeEvidence(file)));
      setEvidence((current) => [...current, ...assets.map((asset) => asset.id)]);
      setState('ready');
    } catch {
      setState('error');
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function submit() {
    if (!transport || !description.trim() || existing) return;
    setState('submitting');
    try {
      const result = await customerCabinetApi.createDispute({
        deal: transport.deal,
        opened_by_role: 'customer',
        reason,
        description: description.trim(),
        expected_resolution: resolution,
        evidence,
      });
      setExisting(result);
      setState('success');
    } catch {
      setState('error');
    }
  }

  return (
    <AccountLayout screen="dispute">
      <div className="customer-dispute" data-state={state}>
        {transport ? (
          <>
            <CustomerPageHeading
              figmaNode={nodes.heading}
              title="Что-то пошло не так?"
              subtitle={<>Заявка #{transport.request_code} · {transport.from_city} → {transport.to_city} · перевозчик <ProfileLink profileId={transport.carrier_id} role="carrier">{transport.carrier_name}</ProfileLink></>}
            />
            <div className="customer-dispute__back" data-figma-node={nodes.back}>
              <Button icon="left" kind="tertiary" size="medium" onClick={() => navigateCustomer('chat', { transport: transport.id })}>Назад к чату</Button>
            </div>
            <div className="customer-dispute__content" data-figma-node={nodes.content}>
              <div className="customer-dispute__alert" data-figma-node={nodes.alert}>⚠ Подавайте только если реально есть проблема. Модератор LOVO разберёт за 24 часа. До разбора чат остаётся открыт.</div>
              <section className="customer-dispute__card" data-figma-node={nodes.card}>
                <div className="customer-dispute__field customer-dispute__reason-field">
                  <span>Причина спора</span>
                  <div className="customer-dispute__reasons">
                    {DISPUTE_REASONS.map(([value, title, subtitle]) => (
                      <Tile active={reason === value} description={subtitle} key={value} title={title} onClick={() => setReason(value)} />
                    ))}
                  </div>
                </div>
                <label className="customer-dispute__field customer-dispute__description">
                  <span><span>Комментарий</span><span>Что произошло?</span></span>
                  <textarea placeholder={'Например: «Получили собаку в крайне нервном состоянии...».\nЧем подробнее — тем быстрее разберётся модератор.'} value={description} onChange={(event) => setDescription(event.target.value)} />
                </label>
                <div className="customer-dispute__field customer-dispute__evidence">
                  <span>Приложите доказательства</span>
                  <Button className="customer-dispute__upload" kind="secondary" size="medium" onClick={() => fileInput.current?.click()}><Paperclip aria-hidden="true" size={18} /> Фото, видео, скриншоты переписки{evidence.length ? ` · ${evidence.length}` : ''}</Button>
                  <input ref={fileInput} hidden multiple type="file" accept="image/jpeg,image/png,image/webp,video/mp4" onChange={(event) => void upload(event.target.files)} />
                </div>
                <div className="customer-dispute__field customer-dispute__resolution-field">
                  <span>Что хотите получить</span>
                  <div className="customer-dispute__resolutions">{DISPUTE_RESOLUTIONS.map((value) => <Tile active={resolution === value} key={value} size="small" title={value} onClick={() => setResolution(value)} />)}</div>
                </div>
                <div className="customer-dispute__info">После подачи модератор LOVO свяжется с обеими сторонами в течение 24 часов. Чат с перевозчиком останется открыт.</div>
              </section>
              <div className="customer-dispute__actions" data-figma-node={nodes.actions}>
                <Button className="customer-dispute__cancel" kind="secondary" onClick={() => navigateCustomer('chat', { transport: transport.id })}>Отмена</Button>
                <Button className="customer-dispute__submit" disabled={Boolean(existing) || state === 'submitting'} onClick={() => void submit()}>{existing ? <><CustomerStatus status={existing.status} /> Спор #{existing.id}</> : 'Подать спор'}</Button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AccountLayout>
  );
}

const NOTIFICATION_BLOCK_NODES: Record<FigmaBreakpoint, { heading: string; list: string }> = {
  '1920': { heading: '2680:39771', list: '2680:39777' },
  '1440': { heading: '2680:40510', list: '2680:40516' },
  '768': { heading: '2597:17446', list: '2597:17452' },
  '375': { heading: '2597:17585', list: '2597:17591' },
};

type Channel = 'push' | 'email' | 'sms';
type PreferenceKey = 'new_offers' | 'chat_messages' | 'public_questions' | 'checkpoints' | 'disputes' | 'news';
type Preferences = Record<PreferenceKey, Record<Channel, boolean>>;

const DEFAULT_PREFERENCES: Preferences = {
  new_offers: { push: true, email: true, sms: false },
  chat_messages: { push: true, email: true, sms: false },
  public_questions: { push: true, email: false, sms: false },
  checkpoints: { push: true, email: false, sms: true },
  disputes: { push: true, email: true, sms: true },
  news: { push: false, email: false, sms: false },
};

const CUSTOMER_NOTIFICATION_LABELS: Record<PreferenceKey, string> = {
  new_offers: 'Новые отклики на мою заявку',
  chat_messages: 'Сообщения в чате',
  public_questions: 'Вопросы к заявке',
  checkpoints: 'Точки маршрута и фото в пути',
  disputes: 'Спорные ситуации',
  news: 'Новости LOVO',
};

function normalizePreferences(profile: CustomerProfileDto): Preferences {
  return Object.fromEntries((Object.keys(DEFAULT_PREFERENCES) as PreferenceKey[]).map((event) => [
    event,
    { ...DEFAULT_PREFERENCES[event], ...(profile.notification_preferences?.[event] ?? {}) },
  ])) as Preferences;
}

function NotificationsView() {
  const chatStatus = useChatStatus();
  const { language } = useLanguage();
  const breakpoint = useFigmaBreakpoint();
  const nodes = NOTIFICATION_BLOCK_NODES[breakpoint];
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'error'>('loading');

  async function load() {
    const [notifs, profile] = await Promise.all([
      customerCabinetApi.notifications(),
      customerCabinetApi.profile(),
    ]);
    setNotifications(notifs);
    setPreferences(normalizePreferences(profile));
    setState('ready');
  }

  useEffect(() => { load().catch(() => setState('error')); }, []);

  async function read(item: NotificationDto, groupedIds?: number[]) {
    const idsToMark = groupedIds && groupedIds.length > 0 ? groupedIds : [item.id];
    const unreadIds = idsToMark.filter((id) => {
      const target = notifications.find((n) => n.id === id);
      return !target?.read_at;
    });

    if (unreadIds.length > 0) {
      setState('submitting');
      await Promise.allSettled(unreadIds.map((id) => customerCabinetApi.markNotificationRead(id)));
      await load();
    }
    await chatStatus.refresh();
    if (item.notification_type === 'chat_message' && item.target_id) {
      navigateCustomer('chat', { chat: item.target_id });
    }
    if (item.notification_type === 'public_question' && item.target_id) {
      navigateCustomer('request-detail', { id: item.target_id, section: 'questions' });
    }
  }

  async function readAll() {
    setState('submitting');
    try {
      await customerCabinetApi.markAllNotificationsRead();
      await load();
      await chatStatus.refresh();
    } catch {
      setState('error');
    }
  }

  async function togglePreference(key: PreferenceKey, channel: Channel) {
    if (savingPreferences) return;
    const previous = preferences;
    const next = {
      ...preferences,
      [key]: { ...preferences[key], [channel]: !preferences[key][channel] },
    };
    setPreferences(next);
    setSavingPreferences(true);
    try {
      const profile = await customerCabinetApi.updateProfile({ notification_preferences: next });
      setPreferences(normalizePreferences(profile));
    } catch {
      setPreferences(previous);
    } finally {
      setSavingPreferences(false);
    }
  }

  return (
    <AccountLayout screen="notifications">
      <div className="customer-notifications" data-state={state}>
        <CustomerPageHeading
          action={
            <div className="customer-notifications__actions">
              <Button
                className="customer-notifications__settings-btn"
                kind="secondary"
                size="medium"
                onClick={() => setSettingsOpen((open) => !open)}
              >
                Настроить
              </Button>
              <Button
                className="customer-notifications__read-all"
                kind="secondary"
                size="medium"
                onClick={() => void readAll()}
              >
                Прочитать все
              </Button>
            </div>
          }
          figmaNode={nodes.heading}
          title="Уведомления"
          subtitle="Все события по вашим заявкам"
        />
        {settingsOpen ? (
          <NotificationMatrixCard
            categories={CUSTOMER_NOTIFICATION_LABELS}
            disabled={savingPreferences}
            preferences={preferences}
            onTogglePreference={(key, channel) => void togglePreference(key as PreferenceKey, channel)}
          />
        ) : null}
        <UnifiedNotificationList
          role="customer"
          state={state}
          figmaNode={nodes.list}
          items={notifications.map((item) => ({
            ...item,
            isLink: Boolean(['chat_message', 'public_question'].includes(item.notification_type) && item.target_id),
            actionText: Boolean(item.notification_type === 'chat_message' && item.target_id)
              ? (language === 'en' ? 'Open chat' : 'Открыть чат')
              : item.notification_type === 'public_question' && item.target_id
                ? (language === 'en' ? 'Open question' : 'Открыть вопрос')
                : (language === 'en' ? 'Mark as read' : 'Пометить прочитанным'),
          }))}
          language={language}
          onItemClick={(item, groupedIds) => void read(item, groupedIds)}
        />
      </div>
    </AccountLayout>
  );
}

const PREFERENCE_ROWS: Array<{
  key: PreferenceKey;
  title: { desktop: string; tablet: string; mobile: string };
  subtitle: { desktop: string; tablet: string };
}> = [
  {
    key: 'new_offers',
    title: { desktop: 'Новые отклики на мою заявку', tablet: 'Новые отклики на мою заявку', mobile: 'Новые отклики' },
    subtitle: { desktop: 'Когда перевозчик отправил предложение', tablet: 'Когда перевозчик отправил предложение' },
  },
  {
    key: 'chat_messages',
    title: { desktop: 'Сообщения в чате', tablet: 'Сообщения в чате', mobile: 'Сообщения в чате' },
    subtitle: { desktop: 'Новое сообщение от перевозчика', tablet: 'Новое сообщение от перевозчика' },
  },
  {
    key: 'public_questions',
    title: { desktop: 'Публичные вопросы к заявке', tablet: 'Публичные вопросы к заявке', mobile: 'Вопросы к заявке' },
    subtitle: { desktop: 'Перевозчик задал вопрос, чтобы уточнить', tablet: 'Перевозчик задал вопрос' },
  },
  {
    key: 'checkpoints',
    title: { desktop: 'Контрольные точки перевозки', tablet: 'Контрольные точки перевозки', mobile: 'Контрольные точки' },
    subtitle: { desktop: 'Новое фото или обновление статуса от перевозчика', tablet: 'Новое фото или статус' },
  },
  {
    key: 'disputes',
    title: { desktop: 'Спорные ситуации', tablet: 'Спорные ситуации', mobile: 'Спорные ситуации' },
    subtitle: { desktop: 'Решения модератора по вашему спору', tablet: 'Решения модератора по спору' },
  },
  {
    key: 'news',
    title: { desktop: 'Новости и рекомендации LOVO', tablet: 'Новости и рекомендации LOVO', mobile: 'Новости LOVO' },
    subtitle: { desktop: 'Раз в неделю — обновления сервиса, советы, промо-направления', tablet: 'Раз в неделю — обновления сервиса' },
  },
];

const SETTINGS_BLOCK_NODES: Record<FigmaBreakpoint, {
  content?: string;
  heading: string;
  locale: string;
  notifications: string;
  password: string;
  root?: string;
  tabs?: string;
}> = {
  '1920': { content: '2680:39675', heading: '2680:39671', locale: '2680:39752', notifications: '2680:39676', password: '2680:39742' },
  '1440': { content: '2680:40414', heading: '2680:40410', locale: '2680:40491', notifications: '2680:40415', password: '2680:40481' },
  '768': { heading: '2595:17446', locale: '2595:17538', notifications: '2595:17456', password: '2595:17528', root: '2595:17445', tabs: '2595:17449' },
  '375': { heading: '2596:17447', locale: '2596:17525', notifications: '2596:17457', password: '2596:17516', root: '2596:17446', tabs: '2596:17450' },
};

function passwordAgeLabel(value: string | null | undefined) {
  if (!value) return 'недавно';
  const days = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 86_400_000));
  if (days < 1) return 'сегодня';
  if (days < 30) return `${days} дн. назад`;
  const months = Math.max(1, Math.round(days / 30));
  const ending = months % 10 === 1 && months % 100 !== 11 ? 'месяц' : months % 10 >= 2 && months % 10 <= 4 && (months % 100 < 10 || months % 100 >= 20) ? 'месяца' : 'месяцев';
  return `${months} ${ending} назад`;
}


function SettingsView() {
  const { language, setLanguage } = useLanguage();
  const breakpoint = useFigmaBreakpoint();
  const nodes = SETTINGS_BLOCK_NODES[breakpoint];
  const isDesktop = breakpoint === '1920' || breakpoint === '1440';
  const isMobile = breakpoint === '375';
  const [profile, setProfile] = useState<CustomerProfileDto | null>(null);
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | 'unsupported'>(() => (
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  ));
  const [passwordStage, setPasswordStage] = useState<'current' | 'new'>('current');
  const [currentPassword, setCurrentPassword] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'success' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    customerCabinetApi.profile().then((next) => {
      if (!active) return;
      setProfile(next);
      setPreferences(normalizePreferences(next));
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions) return undefined;
    let active = true;
    let permissionStatus: PermissionStatus | undefined;
    const syncPermission = () => {
      if (!active || !permissionStatus) return;
      setBrowserPermission(permissionStatus.state === 'prompt' ? 'default' : permissionStatus.state);
    };
    navigator.permissions.query({ name: 'notifications' as PermissionName }).then((status) => {
      permissionStatus = status;
      syncPermission();
      status.addEventListener('change', syncPermission);
    }).catch(() => undefined);
    return () => {
      active = false;
      permissionStatus?.removeEventListener('change', syncPermission);
    };
  }, []);

  async function checkBrowserPermission() {
    if (typeof Notification === 'undefined') {
      setBrowserPermission('unsupported');
      return;
    }
    let perm = Notification.permission;
    if (perm === 'default') {
      perm = await Notification.requestPermission();
    } else if (perm === 'denied') {
      alert('Уведомления заблокированы в браузере. Чтобы включить их, нажмите на иконку настроек сайта слева в адресной строке браузера и разрешите уведомления.');
    }
    setBrowserPermission(perm);

    if (perm === 'granted') {
      try {
        new Notification('LOVO', {
          body: 'Уведомления успешно подключены!',
          icon: '/figma/assets/logo.svg',
        });
      } catch {
        // Fallback for environments where Notification constructor requires worker
      }
    }
  }

  async function savePreferences(next: Preferences) {
    setPreferences(next);
    setState('submitting');
    try {
      const updated = await customerCabinetApi.updateProfile({
        notification_preferences: next,
        push_enabled: Object.entries(next).some(([key, channels]) => key !== 'news' && channels.push),
        sms_active_transport: next.checkpoints.sms,
        email_marketing: next.news.email,
      });
      setProfile(updated);
      setPreferences(normalizePreferences(updated));
      setState('ready');
    } catch {
      setState('error');
    }
  }

  function togglePreference(event: PreferenceKey, channel: Channel) {
    if (event === 'disputes') return;
    const next = {
      ...preferences,
      [event]: { ...preferences[event], [channel]: !preferences[event][channel] },
    };
    void savePreferences(next);
  }

  async function changePassword() {
    if (!passwordValue) return;
    if (passwordStage === 'current') {
      setCurrentPassword(passwordValue);
      setPasswordValue('');
      setPasswordStage('new');
      return;
    }
    setState('submitting');
    try {
      await customerCabinetApi.changePassword(currentPassword, passwordValue);
      setCurrentPassword('');
      setPasswordValue('');
      setPasswordStage('current');
      setState('success');
    } catch {
      setState('error');
    }
  }

  async function saveLocale(payload: Record<string, string>) {
    setState('submitting');
    try {
      const updated = await customerCabinetApi.updateProfile(payload);
      setProfile(updated);
      setState('ready');
    } catch {
      setState('error');
    }
  }

  return (
    <AccountLayout screen="settings">
      <div className="customer-settings" data-figma-node={nodes.root} data-state={state}>
        <CustomerPageHeading figmaNode={nodes.heading} title="Настройки" subtitle={isMobile ? 'Уведомления, пароль и язык' : 'Уведомления, пароль и язык интерфейса'} />
        <nav className="customer-settings__tabs" data-figma-node={nodes.tabs} aria-label="Разделы настроек">
          <button type="button" onClick={() => document.getElementById('settings-notifications')?.scrollIntoView({ behavior: 'smooth' })}>🔔 Уведомления</button>
          <button type="button" onClick={() => document.getElementById('settings-password')?.scrollIntoView({ behavior: 'smooth' })}>🔒 Пароль</button>
          <button type="button" onClick={() => document.getElementById('settings-locale')?.scrollIntoView({ behavior: 'smooth' })}>🌐 {isMobile ? 'Язык' : 'Язык и регион'}</button>
        </nav>
        <div className="customer-settings__content" data-figma-node={nodes.content}>
          <section id="settings-notifications" className="customer-settings__card is-notifications" data-figma-node={nodes.notifications}>
            <header>
              <h2>{isDesktop ? <Bell aria-hidden="true" /> : '🔔'}<span>Уведомления</span></h2>
              <p>{isMobile ? 'Push, Email, SMS — выберите каналы для каждого события.' : isDesktop ? 'Как и о чём мы вам сообщаем. Push — в браузере и приложении, SMS — только для критичных событий.' : 'Push — в браузере и приложении, SMS — только для критичных событий.'}</p>
            </header>
            <div className="customer-settings__table">
              <div className="customer-settings__table-head"><span /><span>Push</span><span>Email</span><span>SMS</span></div>
              {PREFERENCE_ROWS.map((row) => {
                const title = isDesktop ? row.title.desktop : isMobile ? row.title.mobile : row.title.tablet;
                const subtitle = isDesktop ? row.subtitle.desktop : row.subtitle.tablet;
                return (
                <div className="customer-settings__row" key={row.key}>
                  <span><strong>{title}</strong>{!isMobile ? <small>{subtitle}</small> : null}</span>
                  {(['push', 'email', 'sms'] as Channel[]).map((channel) => (
                    <Checkbox
                      aria-label={`${title}: ${channel}`}
                      checked={preferences[row.key][channel]}
                      className="customer-settings__channel"
                      disabled={row.key === 'disputes' || state === 'submitting'}
                      key={channel}
                      onChange={() => togglePreference(row.key, channel)}
                    >{channel === 'push' ? 'Push' : channel === 'email' ? 'Email' : 'SMS'}</Checkbox>
                  ))}
                </div>
              );})}
            </div>
            <div className="customer-settings__hint">{isDesktop ? <Lightbulb aria-hidden="true" /> : <span aria-hidden="true">💡</span>}<span>{isMobile ? 'Спорные ситуации нельзя отключить.' : 'Спорные ситуации приходят по всем каналам всегда — нельзя отключить.'}</span></div>
            <div className="customer-settings__permissions">
              <div>
                <span>Push-уведомления в браузере:</span>
                <Tag
                  className={browserPermission === 'denied' ? 'lovo-tag--denied' : browserPermission === 'default' ? 'lovo-tag--default' : ''}
                  icon="no"
                  label={browserPermission === 'granted' ? 'Разрешены' : browserPermission === 'denied' ? 'Запрещены' : browserPermission === 'unsupported' ? 'Не поддерживаются' : 'Не выбрано'}
                  type="Awards"
                />
              </div>
              <Button kind="secondary" size="medium" onClick={() => void checkBrowserPermission()}>Проверить разрешения</Button>
            </div>
          </section>

          <div id="settings-password">
            <SecurityCard
              figmaNode={nodes.password}
              onChangePassword={async (currentPass, newPass) => {
                await customerCabinetApi.changePassword(currentPass, newPass);
                const updated = await customerCabinetApi.profile();
                setProfile(updated);
              }}
            />
          </div>

          {profile ? (
            <LocaleSettingsCard
              figmaNode={nodes.locale}
              timezone={profile.timezone}
              displayCurrency={profile.display_currency}
              onSaveLocale={saveLocale}
            />
          ) : null}
          <RoleSettingsCard />
        </div>
      </div>
    </AccountLayout>
  );
}

function CustomerHelpView() {
  return (
    <AccountLayout screen="customer-help">
      <SupportHelpView role="customer" />
    </AccountLayout>
  );
}

export function CustomerAccountScreen({ screen }: { screen: CustomerAccountScreenId }) {
  return (
    <AuthGuard allow={['customer']}>
      {screen === 'review' ? <ReviewView /> : null}
      {screen === 'dispute' ? <DisputeView /> : null}
      {screen === 'notifications' ? <NotificationsView /> : null}
      {screen === 'settings' ? <SettingsView /> : null}
      {screen === 'customer-help' ? <CustomerHelpView /> : null}
    </AuthGuard>
  );
}
